import { Divider, Tab, Tabs, Theme, useMediaQuery } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import * as React from "react";
import { FC, Fragment, useCallback, useEffect, useState } from "react";
import {
  Datagrid,
  Filter,
  FilterProps,
  Identifier,
  List,
  ListContextProvider,
  ListProps,
  TextField,
  TextInput,
  useGetList,
  useListContext,
} from "react-admin";
import { CustomDateField } from "../../../Components/CustomDateField";
import AircraftTypeField from "../../../Components/ReferenceFields/AircraftType/AircraftTypeField";
import AircraftTypeInput from "../../../Components/ReferenceFields/AircraftType/AircraftTypeInput";
import CreatedByField from "../../../Components/ReferenceFields/CreatedBy/CreatedByField";
import CustomerField from "../../../Components/ReferenceFields/Customer/CustomerField";
import CustomerInput from "../../../Components/ReferenceFields/Customer/CustomerInput";
import UpdatedByField from "../../../Components/ReferenceFields/UpdatedBy/UpdatedByField";
import { DefaultCreatedDateSort, DefaultPerPageSize, DefaultPhaseOutFilter } from "../../../Constants/Constant";
import { SourceVwUser } from "../../../Constants/Source";
import { PhaseInOutStatus } from "../../../Enums/Phase";
import { ExcelExport } from "../../../Helper/ExportExcel";
import { TabbedDatagridProps } from "../../../Models/TabbedDataGridProps";
import { SourceAircraftType } from "../AircraftType";
import { SourceCustomer } from "../Customer";
import { AircraftExcelHeader, AircraftExcelName } from "./Constant/Excel";
import { SourceAircraft, TitleAircraft } from "./Constant/Source";
import { useAccessControl } from "@airborne/react-admin";

const ExportToExcel = (records, fetchRelatedRecords) => {
  fetchRelatedRecords(records, "customerId", SourceCustomer).then(customer => {
    fetchRelatedRecords(records, "aircraftTypeId", SourceAircraftType).then(aircraftType => {
      fetchRelatedRecords(records, "createdBy", SourceVwUser).then(createdBy => {
        fetchRelatedRecords(records, "updatedBy", SourceVwUser).then(updatedBy => {
          const data = records.map(record => ({
            ...record,
            customer: customer[record.customerId]?.customerName,
            airCraftTypeCode: aircraftType[record.aircraftTypeId]?.aircraftTypeCode,
            createdBy: `${createdBy[record.createdBy]?.registerId} - ${createdBy[record.createdBy]?.name}`,
            updatedBy: `${updatedBy[record.updatedBy]?.registerId} - ${updatedBy[record.updatedBy]?.name}`,
          }));

          ExcelExport(
            AircraftExcelHeader,
            data.map(v => [
              v.customer,
              v.acRegistration,
              v.airCraftTypeCode,
              v.aircraftMsn,
              v.manufacturatedDate,
              v.joinToFleetDate,
              v.aircraftAge,
              v.createdDate,
              v.updatedDate,
              v.phaseOutDate,
              v.createdBy,
              v.updatedBy,
            ]),
            AircraftExcelName
          );
        });
      });
    });
  });
};

const CustomerFilter: FC<Omit<FilterProps, "children">> = props => (
  <Filter {...props}>
    <CustomerInput label="Customer" source="customerId__eq" alwaysOn fullWidth />
    <TextInput label="A/C Registration" source="acRegistration__contains" alwaysOn fullWidth />
    <AircraftTypeInput label="A/C Type" source="aircraftTypeId__eq" alwaysOn fullWidth />
  </Filter>
);

const useDatagridStyles = makeStyles({
  total: { fontWeight: "bold" },
});

const useGetTotals = (filterValues: any) => {
  const { total: totalIsActive } = useGetList(
    SourceAircraft,
    { perPage: 100, page: 1 },
    { field: "createdDate", order: "DESC" },
    { ...filterValues, phaseOut__eq: false }
  );
  const { total: totalIsPassive } = useGetList(
    SourceAircraft,
    { perPage: 100, page: 1 },
    { field: "createdDate", order: "DESC" },
    { ...filterValues, phaseOut__eq: true }
  );

  return {
    phaseIn: totalIsActive,
    phaseOut: totalIsPassive,
  };
};

const TabbedDatagrid: FC<TabbedDatagridProps> = props => {
  const listContext = useListContext();
  const { ids, filterValues, setFilters, displayedFilters } = listContext;
  const isXSmall = useMediaQuery<Theme>(theme => theme.breakpoints.down("xs"));
  const [phaseIn, setPhaseIn] = useState<Identifier[]>([] as Identifier[]);
  const [phaseOut, setPhaseOut] = useState<Identifier[]>([] as Identifier[]);
  const classes = useDatagridStyles();
  const totals = useGetTotals(filterValues) as any;
  const { canCreate, canEdit, canRead, hasAccess } = useAccessControl(SourceAircraft);

  useEffect(() => {
    if (ids && ids !== filterValues.phaseOut__eq) {
      switch (filterValues.phaseOut__eq) {
        case false:
          setPhaseOut(ids);
          break;
        case true:
          setPhaseIn(ids);
          break;
      }
    }
  }, [ids, filterValues.phaseOut__eq]);

  const handleChange = useCallback(
    (event: React.ChangeEvent<{}>, value: any) => {
      setFilters && setFilters({ ...filterValues, phaseOut__eq: value == 1 }, displayedFilters);
    },
    [displayedFilters, filterValues, setFilters]
  );

  const selectedIds = filterValues.phaseOut__eq == true ? phaseOut : filterValues.phaseOut__eq == false;

  return (
    <Fragment>
      <Tabs
        variant="fullWidth"
        centered
        value={filterValues.phaseOut__eq ? 1 : 0}
        indicatorColor="primary"
        onChange={handleChange}
      >
        {PhaseInOutStatus.map(choice => (
          <Tab
            key={choice.id}
            label={totals[choice.name] ? `${choice.displayName} (${totals[choice.name]})` : choice.displayName}
            value={choice.id}
          />
        ))}
      </Tabs>
      <Divider />
      {isXSmall ? (
        <ListContextProvider value={{ ...listContext, ids: selectedIds }}>
          <></>
        </ListContextProvider>
      ) : (
        <ListContextProvider
          value={{
            ...listContext,
            ids: filterValues.phaseOut_eq ? phaseIn : phaseOut,
          }}
        >
          <Datagrid {...props} optimized rowClick={filterValues.phaseOut__eq === false && canEdit ? "edit" : ""}>
            <CustomerField label="Customer" link={false} />
            <TextField source="acRegistration" label="A/C Registration" />
            <AircraftTypeField label="A/C Type" link={true} />
            <TextField source="aircraftMsn" label="A/C MSN" />
            <CustomDateField source="manufacturatedDate" label="Manufacturated Date" showTime={false} />
            <CustomDateField source="joinToFleetDate" label="Join to Fleet Date" showTime={false} />
            <TextField source="aircraftAge" label="A/C Age" />
            <CustomDateField source="createdDate" label="Activated Date" showTime />
            <CustomDateField source="updatedDate" label="Modified Date" showTime />
            <CustomDateField source="phaseOutDate" label="Deactivated Date" showTime />
            <CreatedByField label="Created By" link={false} />
            <UpdatedByField label="Updated By" link={false} />
          </Datagrid>
        </ListContextProvider>
      )}
    </Fragment>
  );
};

const AircraftList: FC<ListProps> = props => (
  <List
    {...props}
    title={TitleAircraft.list}
    filterDefaultValues={DefaultPhaseOutFilter}
    sort={DefaultCreatedDateSort}
    perPage={DefaultPerPageSize}
    exporter={ExportToExcel}
    filters={<CustomerFilter />}
  >
    <TabbedDatagrid />
  </List>
);

export default AircraftList;
